import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { RUB } from '../../../../../utils/char';
import { formatDistanceToNow } from '../../../../../utils/format/formatDistanceToNow';
import { formatThousandsInt } from '../../../../../utils/format/formatThousands';
import { useIsContentPromotionEnabled } from '../../../../../utils/useIsContentPromotionEnabled';
import { useTranslation } from '../../../../../utils/useTranslation';
import { Tooltip, WithHint } from '../../../../feedback/Tooltip';
import { IconKebab } from '../../../../icon/IconKebab';
import { IconProductWarning } from '../../../../icon/IconProductWarning';
import { Checkbox } from '../../../../input/Checkbox';
import { IconButton } from '../../../../input/IconButton';
import { Switch } from '../../../../input/Switch';
import { Box } from '../../../../layout/Box';
import { Flex } from '../../../../layout/Flex';
import { MediaCover } from '../../../../media/MediaCover';
import { Typography } from '../../../../typography/Typography';
import { MediaPreview } from '../../../MediaPreview';
import { CurrencyIcon } from '../../../ProductMonetization/styles';
import { PostStatus } from '../../../constants';
import { getContentIcon } from '../../../getContentIcon';
import { getSourceDisplayText } from '../../../getSourceDisplayText';
import { DeclinedReason } from '../../../types';
import { getPostType } from '../../../utils/getPostType';
import { ContentColumn, ControlColumn, DateColumn, Row } from '../../styles';
import { FeedManagementAdditionalField } from '../../types';
import { COLOR_MAPPING, ICON_MAPPING, TOOLTIP_TEXT_MAPPING } from './constants';
import { IconWrapper, MonetizationIconWrapper, StatusIconWrapper } from './styles';
import { FeedManagementRowProps } from './types';

const StatusIcon = ({
    status,
    declinedReason,
    monetizationEnabled,
}: {
    status: PostStatus;
    declinedReason?: DeclinedReason;
    monetizationEnabled?: boolean;
}) => {
    const key = status || PostStatus.PUBLISHED;
    let iconComponent = ICON_MAPPING[key];
    let color = COLOR_MAPPING[key];
    let tooltipText = TOOLTIP_TEXT_MAPPING[key];
    const monetizationIcon = monetizationEnabled ? (
        <MonetizationIconWrapper>
            <CurrencyIcon backgroundColor="var(--color-primary)" color="var(--color-invert)">
                {RUB}
            </CurrencyIcon>
        </MonetizationIconWrapper>
    ) : null;

    if (declinedReason === DeclinedReason.NEED_PRODUCT) {
        iconComponent = <IconProductWarning size={20} fill="var(--always-white)" />;
        color = 'var(--color-danger)';
        tooltipText = 'Нет товаров в посте';
    }

    return (
        <WithHint
            trigger={
                <StatusIconWrapper background={color}>
                    {iconComponent}
                    {monetizationIcon}
                </StatusIconWrapper>
            }
            message={tooltipText}
        />
    );
};

export const FeedManagementRow = (props: FeedManagementRowProps) => {
    const t = useTranslation();
    const { selected, post, additionalFields, renderMenu, onSelect, onPromotionToggle, ...other } =
        props;
    const onToggleSelected = useCallback(() => onSelect(!selected), [selected, onSelect]);
    const ContentIcon = getContentIcon(post.content, post.postType);
    const contentPromotionEnabled =
        useIsContentPromotionEnabled() &&
        additionalFields?.[FeedManagementAdditionalField.PROMOTION];

    // Тумблер продвижения должен быть отключен, если статус поста не "опубликован".
    const isPromotionDisabled = post.status !== PostStatus.PUBLISHED;

    const onPromotionChange = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const enabled = (event.target as HTMLInputElement).checked;
            onPromotionToggle?.(post.id, enabled);
        },
        [post.id, onPromotionToggle],
    );

    const stats = post.stats;

    return (
        <Row {...other}>
            <ControlColumn>
                <Checkbox checked={selected} onChange={onToggleSelected} />
            </ControlColumn>
            <ControlColumn>
                <Box
                    width={24}
                    height={24}
                    mr={2}
                    borderRadius={1}
                    backgroundColor={COLOR_MAPPING[post.status ?? PostStatus.PUBLISHED]}
                >
                    <StatusIcon
                        status={post.status}
                        declinedReason={post.declinedReason}
                        monetizationEnabled={post.monetizationEnabled}
                    />
                </Box>
            </ControlColumn>
            <Flex as={Link} to={post.url} position="relative" flexShrink={0} textDecoration="none">
                <MediaCover width={64} ratio={1} borderRadius={1} overflow="hidden">
                    <MediaPreview media={post.content[0]} namespace="grid" />
                </MediaCover>
                {ContentIcon ? (
                    <IconWrapper right={0.5} bottom={0.5}>
                        <ContentIcon color="var(--always-white)" size={20} />
                    </IconWrapper>
                ) : null}
            </Flex>
            <Flex width={70} flexShrink={0}>
                <Typography variant="caption" color="secondary" mx={1}>
                    {getPostType(post.postType)}
                </Typography>
            </Flex>
            <ContentColumn minWidth={72} as={Link} to={post.url} textDecoration="none">
                <Typography variant="caption" lines={2} wordBreak="break-word">
                    {post.text}
                </Typography>
                <Typography variant="caption" color="secondary">
                    {getSourceDisplayText(post.source)}
                </Typography>
            </ContentColumn>
            <ContentColumn
                width={74}
                alignItems="flex-end"
                alignSelf="unset"
                flexGrow={0}
                flexShrink={0}
            >
                <Typography variant="caption">
                    {formatThousandsInt(Number(stats?.showsInGrid?.total.display ?? 0))}
                </Typography>
            </ContentColumn>
            {additionalFields?.[FeedManagementAdditionalField.SHOWS] ? (
                <ContentColumn
                    width={74}
                    alignItems="flex-end"
                    alignSelf="unset"
                    flexGrow={0}
                    flexShrink={0}
                >
                    <Typography variant="caption">
                        {formatThousandsInt(Number(stats?.shows?.total.display ?? 0))}
                    </Typography>
                </ContentColumn>
            ) : null}
            {additionalFields?.[FeedManagementAdditionalField.COVERAGE] ? (
                <ContentColumn
                    width={66}
                    alignItems="flex-end"
                    alignSelf="unset"
                    flexGrow={0}
                    flexShrink={0}
                >
                    <Typography variant="caption">
                        {formatThousandsInt(Number(stats?.coverage?.total.display ?? 0))}
                    </Typography>
                </ContentColumn>
            ) : null}
            {additionalFields?.[FeedManagementAdditionalField.ACCOUNTS_INVOLVED] ? (
                <ContentColumn
                    width={108}
                    alignItems="flex-end"
                    alignSelf="unset"
                    flexGrow={0}
                    flexShrink={0}
                >
                    <Typography variant="caption">
                        {formatThousandsInt(Number(stats?.involved?.total.display ?? 0))}
                    </Typography>
                </ContentColumn>
            ) : null}
            {additionalFields?.[FeedManagementAdditionalField.SUBSCRIBERS] ? (
                <ContentColumn
                    width={101}
                    alignItems="flex-end"
                    alignSelf="unset"
                    flexGrow={0}
                    flexShrink={0}
                >
                    <Typography variant="caption">
                        {formatThousandsInt(Number(stats?.subscribers?.total.display ?? 0))}
                    </Typography>
                </ContentColumn>
            ) : null}
            <ContentColumn
                width={127}
                alignItems="flex-end"
                alignSelf="unset"
                flexGrow={0}
                flexShrink={0}
            >
                <Typography variant="caption">
                    {formatThousandsInt(Number(stats?.interactions?.total.display ?? 0))}
                </Typography>
            </ContentColumn>
            {contentPromotionEnabled ? (
                <Flex width={100} flexShrink={0} justifyContent="center" alignItems="center">
                    {isPromotionDisabled ? (
                        <Tooltip
                            trigger={
                                // Обёртка в Box нужна, чтобы тултип появлялся при наведении на задизейбленный тумблер.
                                <Box display="inline-block">
                                    <Switch checked={false} onChange={onPromotionChange} disabled />
                                </Box>
                            }
                            options={{ placement: 'bottom-start' }}
                            offset={[4, 0]}
                        >
                            <Typography variant="caption" color="var(--color-invert)">
                                {t('Пост не опубликован')}
                            </Typography>
                        </Tooltip>
                    ) : (
                        <Switch
                            checked={post.enabledDefaultBoosting}
                            onChange={onPromotionChange}
                        />
                    )}
                </Flex>
            ) : null}
            <DateColumn as={Link} to={post.url} textDecoration="none" alignSelf="stretch">
                <Typography textAlign="right" variant="caption">
                    {formatDistanceToNow(post.createdAt)}
                </Typography>
            </DateColumn>
            <ControlColumn>
                {renderMenu(
                    <IconButton size="s">
                        <IconKebab />
                    </IconButton>,
                )}
            </ControlColumn>
        </Row>
    );
};
